import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramSensibleHeatRatio } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.heatTransferSHR.v1';

type Fields = { sensibleHeat: string; totalHeat: string; shr: string };
const EMPTY: Fields = { sensibleHeat: '', totalHeat: '', shr: '' };

/**
 * Sensible Heat Ratio — SHR = Q_s / Q_total
 * Solves for whichever of Q_s / Q_total / SHR is missing given the other two.
 */
const HeatTransferSensibleHeatRatioScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const qs = toImp(fields.sensibleHeat, 'heatRate');
    const qt = toImp(fields.totalHeat, 'heatRate');
    const shr = parseFloat(fields.shr);

    if (fields.shr === '') {
      if (qt <= 0) { setError('Total heat must be greater than zero.'); return; }
      if (qs > qt) { setError('Sensible heat cannot exceed total heat.'); return; }
      const shrCalc = qs / qt;
      if (shrCalc > 1) { setError('Sensible heat ratio cannot exceed 1.0.'); return; }
      setSolvedFor('shr');
      set({ shr: shrCalc.toFixed(3) });
    } else if (fields.sensibleHeat === '') {
      if (shr < 0 || shr > 1) { setError('SHR must be between 0 and 1.'); return; }
      setSolvedFor('sensibleHeat');
      set({ sensibleHeat: fromImp(qt * shr, 'heatRate', 0) });
    } else if (fields.totalHeat === '') {
      if (shr <= 0 || shr > 1) { setError('SHR must be between 0 and 1.'); return; }
      setSolvedFor('totalHeat');
      set({ totalHeat: fromImp(qs / shr, 'heatRate', 0) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const shrPct = fields.shr ? `${(parseFloat(fields.shr) * 100).toFixed(1)}%` : null;

  const result = [
    fields.sensibleHeat && { label: 'Sensible Heat', value: fields.sensibleHeat, unit: unit('heatRate'), emphasis: solvedFor === 'sensibleHeat' },
    fields.totalHeat && { label: 'Total Heat', value: fields.totalHeat, unit: unit('heatRate'), emphasis: solvedFor === 'totalHeat' },
    fields.shr && { label: 'SHR', value: fields.shr, emphasis: solvedFor === 'shr' },
    shrPct && { label: 'SHR Percentage', value: shrPct },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Sensible Heat Ratio"
      subtitle="Calculate sensible heat ratio (SHR) from sensible and total heat loads"
      formula={
        <>
          <FormulaLine>SHR = Q_s / Q_total</FormulaLine>
          <FormulaNote>SHR = Sensible Heat Ratio (0–1), Q_s = Sensible Heat ({unit('heatRate')}), Q_total = Total Heat ({unit('heatRate')})</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: SHR = Q_s / Q_total"
      diagram={<DiagramSensibleHeatRatio />}
      outputSlots={{
        sensible_heat_btuh: fields.sensibleHeat ? toImp(fields.sensibleHeat, 'heatRate') : undefined,
        q_btuh: fields.totalHeat ? toImp(fields.totalHeat, 'heatRate') : undefined,
        shr: fields.shr ? parseFloat(fields.shr) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.sensible_heat_btuh != null && !fields.sensibleHeat) patch.sensibleHeat = fromImp(values.sensible_heat_btuh, 'heatRate', 0);
        if (values.q_btuh != null && !fields.totalHeat) patch.totalHeat = fromImp(values.q_btuh, 'heatRate', 0);
        if (values.shr != null && !fields.shr) patch.shr = values.shr.toFixed(3);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Sensible Heat"
          value={fields.sensibleHeat}
          onChangeText={(v) => set({ sensibleHeat: v })}
          rightText={unit('heatRate')}
          maxDecimals={0}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Total Heat"
          value={fields.totalHeat}
          onChangeText={(v) => set({ totalHeat: v })}
          rightText={unit('heatRate')}
          maxDecimals={0}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Sensible Heat Ratio (SHR)"
          value={fields.shr}
          onChangeText={(v) => set({ shr: v })}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default HeatTransferSensibleHeatRatioScreen;
